/* eslint-disable react-hooks/rules-of-hooks */
import commerceConfig from '@commerce/config'
import usePrice from '@commerce/product/use-price'
import { ProductVariant } from '@commerce/types/product'
import { QuantityNew, SelectDropDown } from '@components/ui'
import processLocale from '@lib/locale'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { SelectedOptions } from '../helpers'
import ByLitreProcessOptions from './ByLitreProcessOptions'

interface props {
  disabledAmountSelection?: boolean
  options: ProductVariant[]
  currencyCode: string
  selectedOption: ProductVariant | null
  setSelectedOption: React.Dispatch<React.SetStateAction<ProductVariant | null>>
  setSelectedOptions: React.Dispatch<React.SetStateAction<SelectedOptions>>
  selectedOptions?: SelectedOptions
  isByLitreProcess?: boolean
}

function ProductOptionByLitre({
  disabledAmountSelection,
  options,
  currencyCode,
  selectedOption,
  selectedOptions,
  setSelectedOption,
  setSelectedOptions,
  isByLitreProcess = false,
}: props) {
  const { t } = useTranslation('common')
  const [quantity, setQuantity] = useState(0)

  const { locale } = useRouter()
  const {
    config: { context },
  } = commerceConfig
  const currentLocale = processLocale(locale)
  const currentContext = context?.[currentLocale]
  const taxAmount = currentContext?.taxAmount
  const calculateAfterTaxPrice = (price: number) => {
    const { price: afterTaxPrice } = usePrice({
      amount: price ? price * (1 + taxAmount) : 0,
      currencyCode,
      suffix: `${t('incl-vat-approx')}`,
    })
    return afterTaxPrice
  }

  const calculateListPrice = (price: number) => {
    const { price: listPrice } = usePrice({
      amount: price,
      currencyCode,
    })
    return listPrice
  }
  const { price: afterTaxPrice } = usePrice({
    amount:
      selectedOption?.price && selectedOption?.price * (1 + taxAmount || 1),
    currencyCode,
    suffix: `(${t('incl-vat-approx')})`,
  })

  const listPrice = usePrice({
    amount: selectedOption?.price!,
    currencyCode,
  })

  const handleOptionSelection = (option: { name: string; value: string }) => {
    const newOption = options.find((o) => o.id.toString() === option.value)!
    setSelectedOption(newOption)
    setQuantity(0) // Reset quantity when a new option is selected
    setSelectedOptions({}) // Reset quantity when a new option is selected
  }

  const handleQtyIncrease = () => setQuantity((q) => q + 1)
  const handleQtyDecrease = () => setQuantity((q) => Math.max(0, q - 1))

  useEffect(() => {
    if (selectedOption && quantity > 0) {
      setSelectedOptions({ [selectedOption.id]: quantity })
    } else {
      setSelectedOptions((prevState) => {
        const state = { ...prevState }
        if (selectedOption) {
          delete state[selectedOption.id]
        }
        return state
      })
    }
  }, [quantity, selectedOption, setSelectedOptions])

  if (isByLitreProcess) {
    return (
      <div className="flex flex-col gap-4">
        {options.map((v) => {
          return (
            <ByLitreProcessOptions
              key={v.id}
              option={v}
              currencyCode={currencyCode}
              selectedOptions={selectedOptions || {}}
              setSelectedOptions={setSelectedOptions}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className="flex gap-6">
      <SelectDropDown
        className="min-h-16"
        handleOptionSelection={handleOptionSelection}
        options={options.map((option) => {
          let i18nName = option.name?.replace('By Litre / ', '')
          i18nName = i18nName
            ?.replaceAll('litre', t('litre'))
            .replace('li', 'Li')

          return {
            name: i18nName || '',
            value: option.id.toString(),
            price: taxAmount
              ? calculateAfterTaxPrice(option.price)
              : calculateListPrice(option.price),
            availableForSale: option.availableForSale,
          }
        })}
        placeholder={
          disabledAmountSelection
            ? t('choose-surface-first')
            : t('choose-a-size')
        }
        productDropdown
        selectedOption={{
          name: selectedOption?.name || '',
          value: selectedOption?.id.toString() || '',
          price: taxAmount ? afterTaxPrice : listPrice.price,
        }}
        disabled={disabledAmountSelection}
      />

      <QuantityNew
        allowZero
        value={quantity}
        increase={handleQtyIncrease}
        decrease={handleQtyDecrease}
        className="min-w-22 justify-between border-ui-grey-25 bg-white"
        disabled={!selectedOption}
        disabledAmountSelection={disabledAmountSelection}
      />
    </div>
  )
}

export default ProductOptionByLitre
